import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { get } from "lodash";
import moment from "moment";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import DatePicker from "components/datePicker";
import DateTimeFormatPicker from "components/dateTimeFormatPicker";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ActionMultiAttendantPayload,
  ActionSingleAttendantPayload,
  AttendantType,
  DetailGroupType,
  EditGroupPayload,
  EventType,
  PagingType,
} from "routes/group/types";
import { SelectedAttendantType } from "routes/group/pages/edit";
import { groupApi } from "routes/group/api/group.service";
import { DownloadIcon, UploadIcon } from "@heroicons/react/outline";
import { GroupStatusTypes, IUserData } from "interfaces";
import {
  PHONE_CODE,
  REGEX_PHONE_NUMBER,
  REGEX_PHONE_NUMBER_MIN_5,
} from "utills/constants";
import { CollapsibleSection } from "components/collapsibleSection";
import { Label } from "components/label";
import { isHaveActionPermission } from "utills/helpers";
import { useAttendantExportExcel } from "routes/group/hooks/useAttendantExportExcel";
import useQueryParams from "routes/group/hooks/useQueryParams";
import Button from "components/button";
import Input from "components/input";
import Select from "components/select";
import AttendantDataTable, { FilterType } from "../AttendantDataTable";
import VatDetailForm from "../VatDetailForm/VatDetailForm";

dayjs.extend(customParseFormat);

interface PropType {
  user: IUserData | null;
  data: DetailGroupType;
  events: EventType[];
  loading: boolean;
  attendants: AttendantType[];
  attendantPaging: PagingType | null;
  handleSubmitForm: (payload: EditGroupPayload) => void;
  handleChangeAttendantPage: (page: number) => void;
  attendantFilter: FilterType;
  handleChangeAttendantFilter: (key: string, value: string) => void;
  handleSearchAttendant: (data: FilterType) => Promise<void>;
  handleActionSingleAttendant: (
    group_id: string,
    payload: ActionSingleAttendantPayload,
  ) => Promise<void>;
  selectedAttendantIds: SelectedAttendantType[];
  toggleSelectAttendantId: (item: SelectedAttendantType) => void;
  toggleSelectMultiAttendantId: (items: SelectedAttendantType[]) => void;
  handleClearAllSelectAttendantId: () => void;
  handleActionMultiAttendant: (
    group_id: string,
    payload: ActionMultiAttendantPayload,
  ) => Promise<void>;
  handleRefetchGroupAttendant: () => void;
  loadingImport: boolean;
  handleChooseFile: () => Promise<void>;
  handleRefetchDetailGroup: () => void;
}

const GroupForm = (props: PropType) => {
  const {
    user,
    data,
    events,
    loading,
    attendants,
    attendantPaging,
    handleSubmitForm,
    handleChangeAttendantPage,
    attendantFilter,
    handleChangeAttendantFilter,
    handleSearchAttendant,
    handleActionSingleAttendant,
    selectedAttendantIds,
    toggleSelectAttendantId,
    toggleSelectMultiAttendantId,
    handleClearAllSelectAttendantId,
    handleActionMultiAttendant,
    handleRefetchGroupAttendant,
    loadingImport,
    handleChooseFile,
    handleRefetchDetailGroup,
  } = props;

  const ORDER_STATUS_OPTIONS = [
    { value: "1", label: "Opening", color: GroupStatusTypes.OPENING },
    {
      value: "2",
      label: "Send payment",
      color: GroupStatusTypes.SENDPAYMENT,
    },
    { value: "3", label: "Closed", color: GroupStatusTypes.CLOSED },
    { value: "4", label: "Success", color: GroupStatusTypes.SUCCESS },
    { value: "0", label: "Canceled", color: GroupStatusTypes.CANCELED },
  ];

  const { loading: exportLoading, fetchData: exportFetchData } =
    useAttendantExportExcel();

  const { searchParams, setQueryParams } = useQueryParams();

  const infoSectionParam = searchParams.get("open_info_section") ?? "true";
  const attendantSectionParam =
    searchParams.get("open_attendant_section") ?? "true";
  const vatSectionParam = searchParams.get("open_vat_section") ?? "true";

  const handleGetSectionStatus = (value: string) => {
    if (value === "true") return true;
    if (value === "false") return false;
  };

  const formatBoolean = (value: boolean) => {
    if (value) return "true";
    else return "false";
  };

  const [infoSectionOpen, setInfoSectionOpen] = useState<boolean>(
    handleGetSectionStatus(infoSectionParam) ?? true,
  );
  const [attendantsSectionOpen, setAttendantsSectionOpen] = useState<boolean>(
    handleGetSectionStatus(attendantSectionParam) ?? true,
  );
  const [vatSectionOpen, setVatSectionOpen] = useState<boolean>(
    handleGetSectionStatus(vatSectionParam) ?? true,
  );

  const [exportId, setExportId] = useState<string | null>();

  const FormSchema = z.object({
    status: z.string().min(1, { message: "This field is required" }),
    group_name_en: z.string(),
    event_id: z.string(),
    leader_name: z.string().min(1, { message: "This field is required" }),
    leader_email: z
      .string()
      .min(1, { message: "This field is required" })
      .email({ message: "Invalid email address" }),
    leader_phone: z
      .string()
      .min(1, "This field is required")
      .refine(
        (value) => REGEX_PHONE_NUMBER_MIN_5.test(value ?? ""),
        "Invalid phone number",
      ),
    leader_phone_area_code: z.string().min(1, "This field is required"),
    leader_id_card: z.string().min(1, "This field is required"),
    leader_dob: z.string().min(1, "This field is required"),
    customer_code: z.string(),
    id: z.string(),
    created_at: z.string(),
    expired_date: z.string().min(1, "This field is required"),
    num_of_mem: z.string(),
    group_token: z.string(),
    final_amount: z.string(),
    discount_amount: z.string(),
    total_amount: z.string(),
  });

  type FormType = z.infer<typeof FormSchema>;

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    setFocus,
    setValue,
    reset,
    clearErrors,
  } = useForm<FormType>({
    resolver: zodResolver(FormSchema),
    defaultValues: data
      ? ({
          ...data,
          status: data?.status?.toString(),
          num_of_mem: data?.num_of_mem?.toString() ?? "",
          leader_phone: data?.leader_phone?.phone,
          leader_phone_area_code: data?.leader_phone?.code,
          leader_dob: data?.leader_dob,
          created_at: data?.created_at
            ? dayjs(data?.created_at).format("DD/MM/YYYY HH:mm:ss")
            : "",
          expired_date: data?.expired_date
            ? dayjs(data?.expired_date).format("DD/MM/YYYY HH:mm:ss")
            : "",
          final_amount:
            data?.summary_group_data?.final_amount?.toString() ?? "",
          discount_amount:
            data?.summary_group_data?.discount_amount?.toString() ?? "",
          total_amount:
            data?.summary_group_data?.total_amount?.toString() ?? "",
        } as any)
      : {
          status: 0,
          group_name_en: "",
          event_id: "",
          leader_name: "",
          leader_email: "",
          leader_phone: "",
          leader_phone_area_code: "",
          leader_id_card: "",
          leader_dob: "",
          customer_code: "",
          id: "",
          created_at: "",
          expired_date: "",
          num_of_mem: "",
          group_token: "",
          final_amount: "",
          discount_amount: "",
          total_amount: "",
        },
  });

  const handleGetEventInfomation = (event_id: string, events: EventType[]) => {
    if (!events) return null;

    return events?.find((event) => event?.id === event_id) ?? null;
  };

  const onSubmit = (data: any) => {
    // console.log("Dữ liệu hợp lệ:", data);

    if (!user) {
      toast.error("Unable to identify the user");
      return;
    }

    const payload: EditGroupPayload = {
      group_id: data?.id,
      update_by: user?.email,
      data: {
        order_status: Number(data?.status),
        expired_date: data?.expired_date
          ? moment(data?.expired_date, "DD/MM/YYYY HH:mm:ss", true).format(
              "YYYY-MM-DD HH:mm:ss",
            )
          : "",
        leader_name: data?.leader_name,
        leader_phone: data?.leader_phone,
        leader_phone_area_code: data?.leader_phone_area_code,
        leader_email: data?.leader_email,
        leader_dob: data?.leader_dob,
        leader_id_card: data?.leader_id_card,
      },
    };

    handleSubmitForm(payload);
  };

  const onError = (errors: any) => {
    console.error("❌ Lỗi submit:", errors);

    const firstErrorKey = Object.keys(errors)[0];
    setFocus(firstErrorKey as any);
  };

  const handleExportExcel = async () => {
    const res = await exportFetchData({
      // event_id: data?.event_id,
      group_id: data?.id,
      q: attendantFilter.q,
      payment_status: attendantFilter.payment_status,
    });

    if (res.is_success) setExportId(res.export_id);
  };

  // Handle Export Excel
  useEffect(() => {
    let intervalId: any;

    if (exportId) {
      intervalId = setInterval(() => {
        groupApi.getExportResult(exportId).then((data) => {
          const isSuccess = get(data, "data.data.is_success", false);
          if (isSuccess) {
            setExportId(null);
            window.location.href = get(data, "data.data.file_link", "#");
          }
        });
      }, 2000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [exportId, groupApi.getExportResult]);

  return (
    <div className="wrapper">
      <CollapsibleSection
        customOpen={{
          open: infoSectionOpen,
          onClick: () => {
            setInfoSectionOpen(!infoSectionOpen);
            setQueryParams({
              open_info_section: formatBoolean(!infoSectionOpen),
            });
          },
        }}
        label={"Group Information"}
      >
        <form
          onSubmit={handleSubmit(onSubmit, onError)}
          className="p-6 flex flex-col gap-5"
        >
          {/* General Information Section */}
          <h3 className="col-span-full text-[0.9rem] font-bold text-gray-600">
            General Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-8">
            {/* Status */}
            <Controller
              name="status"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <div className="w-full flex flex-col gap-1">
                    <Label
                      id={"status"}
                      name={"Registration Status"}
                      required
                    />

                    <Select
                      error={fieldState.error}
                      options={ORDER_STATUS_OPTIONS}
                      id="status"
                      value={ORDER_STATUS_OPTIONS.filter(
                        (item) => item.value === field.value,
                      )}
                      placeholder="Select Status"
                      onChange={(selected: {
                        label: string;
                        value: string;
                      }) => {
                        setValue("status", selected.value);
                        clearErrors("status");
                      }}
                    />

                    {fieldState.error && (
                      <span className="error-text font-medium">
                        {fieldState.error.message}
                      </span>
                    )}
                  </div>
                );
              }}
            />

            {/* group_name_en */}
            <Controller
              control={control}
              name="group_name_en"
              render={({ field, fieldState }) => (
                <div className="w-full flex flex-col gap-1">
                  <Label required={true} id="group_name_en" name="Group Name" />

                  <Input
                    {...field}
                    disabled
                    type="text"
                    error={fieldState.error}
                    placeholder="Group Name"
                    className="bg-white"
                  />

                  {fieldState.error && (
                    <span className="error-text font-medium">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              )}
            />

            {/* event_id */}
            <Controller
              control={control}
              name="event_id"
              render={({ field, fieldState }) => {
                const event = handleGetEventInfomation(field.value, events);

                return (
                  <div className="w-full flex flex-col gap-1">
                    <Label required={true} id="event_id" name="Event Name" />

                    <Input
                      {...field}
                      value={get(event, "name_en", "")}
                      disabled
                      type="text"
                      error={fieldState.error}
                      placeholder="Event Name"
                      className="bg-white"
                    />

                    {fieldState.error && (
                      <span className="error-text font-medium">
                        {fieldState.error.message}
                      </span>
                    )}
                  </div>
                );
              }}
            />

            {/* created_at */}
            <Controller
              name="created_at"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <div className="w-full flex flex-col gap-1">
                    <Label id={"created_at"} name={"Created At"} required />

                    <DateTimeFormatPicker
                      value={field.value}
                      disabled
                      onChange={field.onChange}
                      error={fieldState.error}
                      placeholder="Created At"
                      ref={(e) => {
                        e && field.ref(e.input);
                      }}
                    />

                    {fieldState.error && (
                      <span className="error-text font-medium">
                        {fieldState.error.message}
                      </span>
                    )}
                  </div>
                );
              }}
            />

            {/* expired_date */}
            <Controller
              name="expired_date"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <div className="w-full flex flex-col gap-1">
                    <Label id={"expired_date"} name={"Expired At"} required />

                    <DateTimeFormatPicker
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.error}
                      placeholder="Expired At (dd/mm/yyyy hh:mm:ss)"
                      ref={(e) => {
                        e && field.ref(e.input);
                      }}
                    />

                    {fieldState.error && (
                      <span className="error-text font-medium">
                        {fieldState.error.message}
                      </span>
                    )}
                  </div>
                );
              }}
            />

            {/* num_of_mem */}
            <Controller
              control={control}
              name="num_of_mem"
              render={({ field, fieldState }) => (
                <div className="w-full flex flex-col gap-1">
                  <Label
                    required={true}
                    id="num_of_mem"
                    name="Number Of Member"
                  />

                  <Input
                    {...field}
                    disabled
                    type="text"
                    error={fieldState.error}
                    placeholder="Number Of Member"
                    className="bg-white"
                  />

                  {fieldState.error && (
                    <span className="error-text font-medium">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              )}
            />

            {/* group_token */}
            <Controller
              control={control}
              name="group_token"
              render={({ field, fieldState }) => (
                <div className="w-full flex flex-col gap-1">
                  <Label required={true} id="group_token" name="Token" />

                  <Input
                    {...field}
                    disabled
                    type="text"
                    error={fieldState.error}
                    placeholder="Token"
                    className="bg-white"
                  />

                  {fieldState.error && (
                    <span className="error-text font-medium">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              )}
            />
          </div>

          <div className="w-full h-[1px] bg-gray-200"></div>

          {/* Leader Information Section */}
          <h3 className="col-span-full text-[0.9rem] font-bold text-gray-600">
            Leader Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-8">
            {/* leader_name */}
            <Controller
              control={control}
              name="leader_name"
              render={({ field, fieldState }) => (
                <div className="w-full flex flex-col gap-1">
                  <Label required={true} id="leader_name" name="Leader Name" />

                  <Input
                    {...field}
                    type="text"
                    error={fieldState.error}
                    placeholder="Leader Name"
                    className="bg-white"
                  />

                  {fieldState.error && (
                    <span className="error-text font-medium">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              )}
            />

            {/* leader_email */}
            <Controller
              control={control}
              name="leader_email"
              render={({ field, fieldState }) => (
                <div className="w-full flex flex-col gap-1">
                  <Label
                    required={true}
                    id="leader_email"
                    name="Leader Email"
                  />

                  <Input
                    {...field}
                    type="text"
                    error={fieldState.error}
                    placeholder="Leader Email"
                    className="bg-white"
                  />

                  {fieldState.error && (
                    <span className="error-text font-medium">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              )}
            />

            {/* leader_phone - leader_phone_area_code */}
            <div className="w-full flex flex-col gap-1">
              <Label required={true} id="leader_phone" name="Leader Phone" />

              <div className="w-full flex flex-col gap-1">
                <div className="flex">
                  <Controller
                    control={control}
                    name="leader_phone_area_code"
                    defaultValue="84"
                    render={({ field: { onChange, value = "", ref } }) => {
                      const areaPhoneCodeArr = PHONE_CODE.map((item) => ({
                        label: item.label,
                        value: item.code,
                      }));
                      return (
                        <Select
                          ref={ref}
                          className="flex-1 w-[160px] !rounded-none inline-flex items-center !rounded-l-md"
                          options={areaPhoneCodeArr}
                          id="buyer_phone_area_code"
                          value={areaPhoneCodeArr.find(
                            (c) => c.value === value,
                          )}
                          onChange={(selected: {
                            label: string;
                            value: string;
                          }) => onChange(selected?.value)}
                          error={errors["leader_phone_area_code"]}
                        />
                      );
                    }}
                  />
                  <Input
                    id="leader_phone"
                    className={`${
                      errors["leader_phone"]
                        ? "border-l-[1px] focus:!ml-0"
                        : "border-l-0"
                    } rounded-l-none focus:border-l-[1px] focus:ml-[-1px] z-[2]`}
                    placeholder="Phone number"
                    error={errors["leader_phone"]}
                    {...register("leader_phone")}
                  />
                </div>

                {errors["leader_phone"] && (
                  <span className="error-text font-medium">
                    {errors["leader_phone"].message}
                  </span>
                )}
              </div>
            </div>

            {/* leader_id_card */}
            <Controller
              control={control}
              name="leader_id_card"
              render={({ field, fieldState }) => (
                <div className="w-full flex flex-col gap-1">
                  <Label
                    required={true}
                    id="leader_id_card"
                    name="Leader ID Card"
                  />

                  <Input
                    {...field}
                    type="text"
                    error={fieldState.error}
                    placeholder="Leader ID Card"
                    className="bg-white"
                  />

                  {fieldState.error && (
                    <span className="error-text font-medium">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              )}
            />

            {/* leader_dob */}
            <Controller
              name="leader_dob"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <div className="w-full flex flex-col gap-1">
                    <Label id={"leader_dob"} name={"Leader DOB"} required />

                    <DatePicker
                      id="start_date"
                      placeholder="Event date"
                      className="bg-white"
                      error={get(errors, `start_date.message`, "")}
                      value={field.value}
                      onChange={(date: any) =>
                        date === null
                          ? field.onChange("")
                          : field.onChange(moment(date).format("YYYY-MM-DD"))
                      }
                    />

                    {fieldState.error && (
                      <span className="error-text font-medium">
                        {fieldState.error.message}
                      </span>
                    )}
                  </div>
                );
              }}
            />

            {/* customer_code */}
            <Controller
              control={control}
              name="customer_code"
              render={({ field, fieldState }) => (
                <div className="w-full flex flex-col gap-1">
                  <Label
                    required={true}
                    id="customer_code"
                    name="Customer Code"
                  />

                  <Link
                    to={`/customer-accounts/detail/${get(
                      data,
                      "customer_code",
                      0,
                    )}`}
                    className="my-auto text-[14px] font-medium text-brand-500 hover:underline break-words"
                  >
                    {field.value}
                  </Link>

                  {fieldState.error && (
                    <span className="error-text font-medium">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              )}
            />
          </div>

          <div className="w-full h-[1px] bg-gray-200"></div>

          {/* Summary Group Data Section */}
          <h3 className="col-span-full text-[0.9rem] font-bold text-gray-600">
            Amount Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-8">
            {/* final_amount */}
            <Controller
              control={control}
              name="final_amount"
              render={({ field, fieldState }) => (
                <div className="w-full flex flex-col gap-1">
                  <Label
                    required={true}
                    id="final_amount"
                    name="Final Amount"
                  />

                  <Input
                    {...field}
                    disabled
                    type="text"
                    error={fieldState.error}
                    placeholder="Final Amount"
                    className="bg-white"
                  />

                  {fieldState.error && (
                    <span className="error-text font-medium">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              )}
            />

            {/* discount_amount */}
            <Controller
              control={control}
              name="discount_amount"
              render={({ field, fieldState }) => (
                <div className="w-full flex flex-col gap-1">
                  <Label
                    required={true}
                    id="discount_amount"
                    name="Discount Amount"
                  />

                  <Input
                    {...field}
                    disabled
                    type="text"
                    error={fieldState.error}
                    placeholder="Discount Amount"
                    className="bg-white"
                  />

                  {fieldState.error && (
                    <span className="error-text font-medium">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              )}
            />

            {/* total_amount */}
            <Controller
              control={control}
              name="total_amount"
              render={({ field, fieldState }) => (
                <div className="w-full flex flex-col gap-1">
                  <Label
                    required={true}
                    id="total_amount"
                    name="Total Amount"
                  />

                  <Input
                    {...field}
                    disabled
                    type="text"
                    error={fieldState.error}
                    placeholder="Total Amount"
                    className="bg-white"
                  />

                  {fieldState.error && (
                    <span className="error-text font-medium">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              )}
            />
          </div>

          {/* group_id */}
          {/* <Controller
            control={control}
            name="id"
            render={({ field, fieldState }) => (
              <div className="w-full flex flex-col gap-1">
                <Label required={true} id="id" name="Group ID" />

                <Input
                  {...field}
                  disabled
                  type="text"
                  error={fieldState.error}
                  placeholder="Group Name"
                  className="bg-white"
                />

                {fieldState.error && (
                  <span className="error-text font-medium">
                    {fieldState.error.message}
                  </span>
                )}
              </div>
            )}
          /> */}

          <div className="col-span-full flex gap-3 items-center justify-end">
            <Button
              type="submit"
              loading={loading}
              className="w-auto h-11 sm:w-[100px]"
            >
              <span>{loading ? "Loading..." : "Save"}</span>
            </Button>
          </div>
        </form>
      </CollapsibleSection>

      <CollapsibleSection
        customOpen={{
          open: attendantsSectionOpen,
          onClick: () => {
            setAttendantsSectionOpen(!attendantsSectionOpen);
            setQueryParams({
              open_attendant_section: formatBoolean(!attendantsSectionOpen),
            });
          },
        }}
        label={"Attendants"}
        toolbar={
          <div className="flex items-center gap-3">
            {isHaveActionPermission("", "group_buy_management", "export") && (
              <Button
                className="w-11 h-11 sm:w-auto"
                loading={exportLoading || (exportId ? true : false)}
                onClick={(e: any) => {
                  e?.stopPropagation();
                  handleExportExcel();
                }}
              >
                <DownloadIcon className="w-4 h-4 text-white" />{" "}
                <span className="hidden sm:block sm:ml-2">
                  Export Attendant
                </span>
              </Button>
            )}

            {isHaveActionPermission("", "group_buy_management", "create") && (
              <Button
                className="w-11 h-11 sm:w-auto"
                loading={loadingImport}
                onClick={(e: any) => {
                  e?.stopPropagation();
                  handleChooseFile();
                }}
              >
                <UploadIcon className="w-4 h-4 text-white" />{" "}
                <span className="hidden sm:block sm:ml-2">
                  Import Attendant
                </span>
              </Button>
            )}
          </div>
        }
      >
        <div className="p-6">
          <AttendantDataTable
            mode="edit"
            data={attendants}
            paging={attendantPaging as PagingType}
            handleChangePage={handleChangeAttendantPage}
            filter={attendantFilter}
            handleChangeFilter={handleChangeAttendantFilter}
            onSubmit={handleSearchAttendant}
            // Option props for edit case
            handleActionSingle={handleActionSingleAttendant}
            selectedAttendantIds={selectedAttendantIds}
            toggleSelectAttendantId={toggleSelectAttendantId}
            toggleSelectMultiAttendantId={toggleSelectMultiAttendantId}
            handleClearAllSelectAttendantId={handleClearAllSelectAttendantId}
            handleActionMulti={handleActionMultiAttendant}
            handleRefetchGroupAttendant={handleRefetchGroupAttendant}
            handleRefetchDetailGroup={handleRefetchDetailGroup}
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        customOpen={{
          open: vatSectionOpen,
          onClick: () => {
            setVatSectionOpen(!vatSectionOpen);
            setQueryParams({
              open_vat_section: formatBoolean(!vatSectionOpen),
            });
          },
        }}
        label={"VAT Information"}
      >
        <div className="p-6">
          <VatDetailForm data={data} />
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default GroupForm;
